import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { SectionHeader } from '../components/ui/SectionHeader';
import { PORTFOLIO_ITEMS } from '../lib/constants';
import styles from './PortfolioPage.module.css';

export const PortfolioPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language === 'en' ? 'en' : 'it') as 'it' | 'en';
  const [ref, visible] = useScrollReveal(0.1);

  useDocumentMetadata(
    t('portfolio.title') + " | NexTrainE",
    t('portfolio.subtitle')
  );

  return (
    <div className={styles.page}>
      {/* ─── Hero Header ─── */}
      <div className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className="container">
          <SectionHeader
            label={t('portfolio.label')}
            title={t('portfolio.title')}
            subtitle={t('portfolio.subtitle')}
          />
        </div>
      </div>

      {/* ─── Portfolio Grid ─── */}
      <section
        className={styles.section}
        ref={ref as React.RefObject<HTMLElement>}
      >
        <div className="container">
          <div className={styles.grid}>
            {PORTFOLIO_ITEMS.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <h3 className={styles.emptyTitle}>{t('portfolio.coming_soon')}</h3>
                <p className={styles.emptyDesc}>{t('portfolio.coming_soon_desc')}</p>
              </div>
            ) : (
              PORTFOLIO_ITEMS.map((item, idx) => (
                <Link
                  key={item.id}
                  to={`/portfolio/${item.id}`}
                  className={`${styles.card} ${visible ? styles.cardVisible : ''}`}
                  style={{ transitionDelay: `${idx * 150}ms` }}
                >
                  {item.thumbnail && (
                    <div className={styles.thumbnail}>
                      {item.status && (
                        <div className={styles.wipBadge}>
                          {item.status[lang]}
                        </div>
                      )}
                      <img
                        src={item.thumbnail}
                        alt={item.title[lang]}
                        className={styles.thumbnailImg}
                        loading="lazy"
                      />
                      <div className={styles.overlay} />
                    </div>
                  )}
                  <div className={styles.body}>
                    <span className={styles.category}>
                      <span className={styles.categoryDot} />
                      {item.category[lang]}
                    </span>
                    <h3 className={styles.title}>{item.title[lang]}</h3>
                    <p className={styles.desc}>{item.desc[lang]}</p>
                    <div className={styles.tags}>
                      {item.tags.map((tag) => (
                        <span key={tag} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
